import { useEffect, useState } from "react";
import { CButton, CCol, CFormGroup, CInput, CLabel, CRow } from "@coreui/react";
import TaxPayer from "../../models/tax-payer";
import TaxPayerEmailSettings from "../../models/tax-payer-email-settings";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, getItem } from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, successAlert } from "../utils/messages";
import Loading from "../indicators/Loading";

const defaults = (): TaxPayerEmailSettings => ({
  port: 587,
  useSsl: false,
  sendEnable: false,
  password: "",
});
const fields = {
  host: "Host",
  port: "Puerto",
  user: "Usuario",
  password: "Contraseña",
  from: "Correo desde",
} as const;

const TaxPayerEmailSettingsForm = ({ taxPayer }: { taxPayer: TaxPayer }) => {
  const [settings, setSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    getItem<TaxPayerEmailSettings>(`/tax_payers/${taxPayer.id}/email_settings/`)
      .then((result) => {
        if (!active) return;
        if (result.status === SUCCESS && result.data)
          setSettings({ ...result.data, password: "" });
        else if (result.status === NOT_FOUND) setSettings(defaults());
        else
          setLoadError(
            result.detail || "No se pudo cargar la configuración de correo.",
          );
      })
      .catch(() => {
        if (active)
          setLoadError("No se pudo cargar la configuración de correo.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [taxPayer.id]);

  const save = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrors({});
    try {
      const result = await createItem<TaxPayerEmailSettings>(
        `/tax_payers/${taxPayer.id}/email_settings/update/`,
        {
          host: settings.host,
          port: settings.port,
          user: settings.user,
          password: settings.password,
          from: settings.from,
          useSsl: settings.useSsl === true,
          sendEnable: settings.sendEnable === true,
        },
      );
      if (result.status !== SUCCESS || !result.data) {
        setErrors(result.errors || {});
        errorAlert(
          result.detail || "No se pudo guardar la configuración de correo.",
        );
        return;
      }
      setSettings({ ...result.data, password: "" });
      successAlert("Configuración de correo guardada.");
    } catch {
      errorAlert("No se pudo guardar la configuración de correo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading height="30vh" />;
  if (loadError)
    return (
      <div className="alert alert-danger" role="alert">
        {loadError}
      </div>
    );

  return (
    <fieldset disabled={submitting}>
      <p className="text-muted">
        Esta configuración de correo se utiliza para todos los timbrados del
        contribuyente.
      </p>
      {Object.entries(fields).map(([key, label]) => {
        const field = key as keyof typeof fields;
        return (
          <CFormGroup key={field}>
            <CRow>
              <CCol md={2}>
                <CLabel htmlFor={`email-settings-${field}`}>{label}:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  id={`email-settings-${field}`}
                  type={
                    field === "password"
                      ? "password"
                      : field === "port"
                        ? "number"
                        : "text"
                  }
                  value={settings[field] ?? ""}
                  placeholder={
                    field === "password" && settings.hasPassword
                      ? "Dejar vacío para conservar la contraseña"
                      : label
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings((previous) => ({
                      ...previous,
                      [field]:
                        field === "port"
                          ? event.target.value
                            ? Number(event.target.value)
                            : undefined
                          : event.target.value,
                    }))
                  }
                />
                <FieldErrors
                  errors={getFieldErrors(field, errors) as string[]}
                />
              </CCol>
            </CRow>
          </CFormGroup>
        );
      })}
      {(
        [
          ["useSsl", "Usar SSL"],
          ["sendEnable", "Habilitar envíos"],
        ] as const
      ).map(([field, label]) => (
        <CFormGroup key={field}>
          <div className="custom-control custom-switch">
            <input
              id={`email-settings-${field}`}
              type="checkbox"
              role="switch"
              className="custom-control-input"
              checked={settings[field] === true}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setSettings((previous) => ({ ...previous, [field]: checked }));
              }}
            />
            <label
              className="custom-control-label"
              htmlFor={`email-settings-${field}`}
            >
              {label}
            </label>
          </div>
          <FieldErrors errors={getFieldErrors(field, errors) as string[]} />
        </CFormGroup>
      ))}
      <div className="d-flex justify-content-end">
        <CButton color="primary" onClick={save}>
          {submitting ? "Guardando..." : "Guardar"}
        </CButton>
      </div>
    </fieldset>
  );
};

export default TaxPayerEmailSettingsForm;
